import React from 'react';
import { Profiles } from 'lib/templates/Feature.BOKF.model';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import { useDictionary } from 'lib/hooks/use-dictionary';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import Button from 'helpers/Button/Button';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import useExperienceEditor from 'lib/use-experience-editor';

export type AdvisorProfileProps = Profiles.DataTypes.AdvisorProfile & {
  fields: {
    PageTitle?: Field<string>;
    MetaDescription?: Field<string>;
    MetaOGDescription?: Field<string>;
    MetaKeywords?: Field<string>;
  };
};

const AdvisorProfile = (props: AdvisorProfileProps) => {
  const { fields = undefined } = props;

  const dict = useDictionary();

  const isEE = useExperienceEditor();

  const AdvisorConnect = dict?.['Feature.Profiles.AdvisorProfileConnect'] || "Let's Connect";

  return (
    <section className="component container spacing-sm">
      <div className="container-inner">
        <div className="p-4 bg-light-background-neutral-variant border border-light-stroke-primary rounded flex flex-col gap-y-4 gap-x-6 md:p-6 md:gap-y-8 lg:p-8 lg:flex-row">
          <div className="flex flex-row gap-x-6 gap-y-8 lg:flex-col lg:gap-y-6 lg:min-w-[208px] lg:max-w-[208px]">
            <ImageWrapper
              field={fields?.Image}
              height="258"
              width="208"
              className="w-1/3 md:w-[164px] lg:w-full object-cover min-w-[114px] max-w-[208px] aspect-[208/258]"
            />
            <div className="flex flex-col w-full xs:w-2/3 md:w-[calc(100%-164px)] lg:w-full gap-2 justify-center lg:justify-normal">
              <h4 className="flex flex-row flex-wrap text-light-fill-tertiary-brand mt-0">
                <Text tag="span" field={fields?.FirstName} />
                &nbsp;
                <Text tag="span" field={fields?.LastName} />
              </h4>

              <Text tag="p" field={fields?.Title} className="p2-medium" />
              <Text
                tag="p"
                field={fields?.['Advisor Team']}
                className="p3 text-light-fill-primary-variant"
              />
              <ImageWrapper
                field={fields?.Logo}
                width="208"
                height="50"
                className="w-[158px] md:w-[208px] md:mt-4"
              />
            </div>
          </div>
          <div className="flex flex-col w-full gap-y-3 md:gap-y-4">
            <Text
              tag="h4"
              field={{ value: AdvisorConnect }}
              className="text-light-fill-primary md:mb-2 lg:mb-0"
            />
            <div className="flex flex-col sm:flex-row gap-y-3 gap-x-6 w-full lg:min-h-[200px]">
              <div className="flex flex-col gap-y-3 md:gap-y-4 flex-1">
                {(fields?.Phone?.value !== '' || isEE) && (
                  <p className="flex flex-row text-light-fill-tertiary-brand gap-x-2 p2-medium">
                    <Text tag="span" field={{ value: 'T' }} />
                    {
                      <LinkWrapper
                        className="p2"
                        field={{
                          value: {
                            href: `tel:+1${fields?.Phone?.value.replace(/-/g, '').trim()}`,
                          },
                        }}
                      >
                        <Text field={fields?.Phone} />
                      </LinkWrapper>
                    }
                  </p>
                )}
                {(fields?.Mobile?.value !== '' || isEE) && (
                  <p className="flex flex-row text-light-fill-tertiary-brand gap-x-2 p2-medium">
                    <Text tag="span" field={{ value: 'M' }} />
                    {
                      <LinkWrapper
                        className="p2"
                        field={{
                          value: {
                            href: `tel:+1${fields?.Mobile?.value.replace(/-/g, '').trim()}`,
                          },
                        }}
                      >
                        <Text field={fields?.Mobile} />
                      </LinkWrapper>
                    }
                  </p>
                )}
                {(fields?.Fax?.value !== '' || isEE) && (
                  <p className="flex flex-row text-light-fill-tertiary-brand gap-x-2 p2-medium">
                    <Text tag="span" field={{ value: 'F' }} />
                    {
                      <LinkWrapper
                        className="p2"
                        field={{
                          value: {
                            href: `tel:+1${fields?.Fax?.value.replace(/-/g, '').trim()}`,
                          },
                        }}
                      >
                        <Text field={fields?.Fax} />
                      </LinkWrapper>
                    }
                  </p>
                )}

                <LinkWrapper
                  className="p2-medium link"
                  field={{
                    value: {
                      href: `mailto:${fields?.Email?.value}`,
                    },
                  }}
                >
                  <Text field={fields?.Email} />
                </LinkWrapper>
              </div>
              <div className="flex flex-col flex-1 gap-y-4">
                <div>
                  {(fields?.Address1 || isEE) && (
                    <RichTextA11yWrapper className="p2" tag="div" field={fields?.Address1} />
                  )}
                  {(fields?.Address2 || isEE) && (
                    <RichTextA11yWrapper className="p2" tag="div" field={fields?.Address2} />
                  )}
                  <p className="p2">
                    <Text field={fields?.City} />
                    ,&nbsp;
                    <Text field={fields?.State?.fields.StateName as Field<string>} />
                    &nbsp;
                    <Text field={fields?.Zip} />
                  </p>
                </div>
                <LinkWrapper className="p2-medium link" field={fields?.Link || {}} />
                <div className="lg:mt-4">
                  <Button type="default" size="full" tag="a" field={fields?.ApplicationLink} />
                </div>
              </div>
            </div>
            <hr className="solid divide-light-stroke-primary my-1 md:my-0"></hr>
            <div>
              <RichTextA11yWrapper
                className="p2 rtaw text-light-fill-primary-variant"
                field={fields?.ProfileBio}
              />
              {(fields?.['ApplicationLink 2'] || isEE) && (
                <div className="mt-4">
                  <Button type="auxiliary" tag="a" field={fields?.['ApplicationLink 2']} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvisorProfile;
